import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import api from "../lib/api";
import { ArrowRight, Crown, Heart, Coin, Briefcase, Compass, Warning, Sparkle, Clock, ArrowsClockwise, Star, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const TAG_META = {
  marriage: { label: "MARRIAGE", color: "bg-rose-200 text-rose-900", Icon: Heart },
  marriage_supportive: { label: "VENUS-LIKE", color: "bg-pink-200 text-pink-900", Icon: Heart },
  wealth_lift: { label: "WEALTH LIFT", color: "bg-emerald-200 text-emerald-900", Icon: Coin },
  fortune_lift: { label: "FORTUNE", color: "bg-amber-200 text-amber-900", Icon: Star },
  promotion: { label: "PROMOTION", color: "bg-indigo-200 text-indigo-900", Icon: Briefcase },
  authority: { label: "AUTHORITY", color: "bg-amber-200 text-amber-900", Icon: Crown },
  transfer_movement: { label: "TRANSFER / MOVE", color: "bg-sky-200 text-sky-900", Icon: Compass },
  enterprise_launch: { label: "ENTERPRISE LAUNCH", color: "bg-orange-200 text-orange-900", Icon: Sparkle },
  sudden_gain: { label: "SUDDEN GAIN", color: "bg-yellow-300 text-yellow-950", Icon: Star },
  self_brand_peak: { label: "SELF-BRAND PEAK", color: "bg-yellow-200 text-yellow-900", Icon: Crown },
  struggle_dusthana: { label: "STRUGGLE 6/8/12", color: "bg-rose-200 text-rose-900", Icon: Warning },
  major_turnaround: { label: "LIFE TURNAROUND", color: "bg-violet-200 text-violet-900", Icon: ArrowsClockwise },
};

const ELEMENT_TINT = {
  fire: "from-amber-50 to-orange-100 border-amber-300",
  earth: "from-emerald-50 to-teal-100 border-emerald-300",
  air: "from-sky-50 to-indigo-100 border-sky-300",
  water: "from-blue-50 to-cyan-100 border-blue-300",
};

const CATEGORY_META = {
  marriage_windows: { Icon: Heart, label: "Marriage windows", color: "text-rose-900", blurb: "When KCD runs through your 7H, your 7th lord's rashi, or natal-Venus rashi — these periods carry the strongest marriage karma." },
  wealth_lift_windows: { Icon: Coin, label: "Wealth lift windows", color: "text-emerald-900", blurb: "Periods when KCD activates your 2H/5H/9H/11H, your wealth-house lords, Jupiter rashis, or 11th-from-Moon — expanded earnings, savings, gains and fortune." },
  sudden_gain_windows: { Icon: Star, label: "Sudden gain / Windfall / Lottery windows", color: "text-yellow-900", blurb: "Periods when KCD activates your 8H — the classical SUDDEN-WEALTH house. Lottery, inheritance, insurance payouts, surprise legal settlements and unearned wealth concentrate here." },
  enterprise_launch_windows: { Icon: Sparkle, label: "Self-brand / Enterprise launch windows", color: "text-orange-900", blurb: "Periods when KCD activates your Lagna rashi, your lagna lord's rashi, or 3H — your identity peaks; the businesses or ventures founded in these years become your personal-brand legacy." },
  promotion_windows: { Icon: Briefcase, label: "Career promotion / authority", color: "text-indigo-900", blurb: "Periods when KCD activates your 10H or 10L's rashi, or Sun-ruled rashis — career-defining moves and visibility." },
  transfer_movement_windows: { Icon: Compass, label: "Transfer / job change / relocation", color: "text-sky-900", blurb: "When KCD passes through 3H/9H/12H or dual signs — job changes, moves and travel concentrate here." },
  struggle_windows: { Icon: Warning, label: "Struggle (6/8/12 dusthana) windows", color: "text-rose-900", blurb: "Dusthana periods test you before they reward — expect karmic clean-up, health/legal/financial work." },
  major_turnaround_windows: { Icon: ArrowsClockwise, label: "Major life-turnaround windows", color: "text-violet-900", blurb: "KCD is uniquely famous for flagging MAJOR life redirections — every element-shift between MDs, 1H/7H activation, and lagna-lord-ruled rashi falls here." },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

/* === LIFE INSIGHTS SUMMARY ===
   Top-of-page synthesis grouping the entire timeline into 5 actionable
   bands. Each card lists windows chronologically — past, present, future. */
const LI_BANDS = [
  {
    key: "business_starts",
    title: "Best time to start a business",
    sub: "Enterprise-launch ADs on self / partnership / career / gains houses",
    grad: "from-amber-50 via-orange-100 to-amber-50",
    accent: "border-amber-400",
    badge: "bg-amber-500 text-amber-950",
    icon: Briefcase,
  },
  {
    key: "gain_windows",
    title: "Best time for gains",
    sub: "wealth_lift / promotion / fortune_lift on H2 / H10 / H11",
    grad: "from-emerald-50 via-teal-100 to-emerald-50",
    accent: "border-emerald-400",
    badge: "bg-emerald-500 text-emerald-50",
    icon: Coin,
  },
  {
    key: "jackpot_windows",
    title: "Lucky jackpot & sudden rise",
    sub: "All 8 classical sudden-wealth signatures",
    grad: "from-yellow-50 via-amber-100 to-yellow-50",
    accent: "border-yellow-500",
    badge: "bg-yellow-500 text-yellow-950",
    icon: Star,
  },
  {
    key: "beware_windows",
    title: "Beware · financial & business decisions",
    sub: "Reversal windows · maraka / dusthana / asset-drainage",
    grad: "from-slate-50 via-zinc-100 to-stone-50",
    accent: "border-slate-500",
    badge: "bg-slate-700 text-slate-50",
    icon: Warning,
  },
  {
    key: "fame_authority",
    title: "Top fame & authority",
    sub: "Viral-tier fame windows (score ≥ 10)",
    grad: "from-fuchsia-50 via-pink-100 to-fuchsia-50",
    accent: "border-fuchsia-400",
    badge: "bg-fuchsia-500 text-fuchsia-50",
    icon: Crown,
  },
];

function LifeInsightsSummary({ li, fmtDate }) {
  // Build available decade chips from the union of all windows. Each decade
  // spans Jan 1 of the decade to Dec 31 of the last year of the decade.
  const allWindows = React.useMemo(() => {
    const out = [];
    LI_BANDS.forEach((b) => (li[b.key] || []).forEach((w) => out.push(w)));
    return out;
  }, [li]);

  const decades = React.useMemo(() => {
    const set = new Set();
    allWindows.forEach((w) => {
      const s = parseInt((w.start || "").slice(0, 4), 10);
      const e = parseInt((w.end || "").slice(0, 4), 10);
      if (!s || !e) return;
      const startDec = Math.floor(s / 10) * 10;
      const endDec = Math.floor(e / 10) * 10;
      for (let d = startDec; d <= endDec; d += 10) set.add(d);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [allWindows]);

  const [activeDecade, setActiveDecade] = React.useState("all");

  const filterByDecade = (items) => {
    if (activeDecade === "all") return items;
    const d = parseInt(activeDecade, 10);
    const decStart = `${d}-01-01`;
    const decEnd = `${d + 9}-12-31`;
    return items.filter((w) => !(w.end < decStart || w.start > decEnd));
  };

  // === Next-24-months horizon strip ===
  // Surface every life-insight window that overlaps today → today + 24 months,
  // sorted chronologically with band metadata so a user instantly sees what's
  // actually actionable in their near future.
  const horizon = React.useMemo(() => {
    const today = new Date();
    const todayISO = today.toISOString().slice(0, 10);
    const future = new Date(today);
    future.setMonth(future.getMonth() + 24);
    const futureISO = future.toISOString().slice(0, 10);
    const items = [];
    LI_BANDS.forEach((band) => {
      (li[band.key] || []).forEach((w) => {
        if (!(w.end < todayISO || w.start > futureISO)) {
          items.push({ ...w, _band: band });
        }
      });
    });
    items.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    return { items, todayISO, futureISO };
  }, [li]);

  return (
    <section
      className="rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#FFF8E7] via-[#FFEACB] to-[#FFD89A] border-2 border-amber-400 shadow-lg"
      data-testid="life-insights-summary"
    >
      <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
        <Sparkle size={14} weight="duotone" /> Life insights · the whole arc at a glance
      </div>
      <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[#3F2D1B] mt-2 leading-tight">
        Your timeline · best windows, lucky jumps & warning bands
      </h2>
      <p className="text-sm text-[#3F2D1B] mt-3 font-light max-w-3xl leading-relaxed">
        A synthesised view of the entire Kalachakra computation. Each band lists the highest-signal windows from your full 99-year timeline in <b>chronological order</b> so you see what's behind you, what's now, and what's coming.
      </p>

      {/* Decade filter chip-bar */}
      {decades.length > 0 && (
        <div className="mt-6" data-testid="life-insights-decade-bar">
          <div className="label-eyebrow text-[#7C2D12] mb-2 text-[10px]">Filter by life-decade</div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveDecade("all")}
              data-testid="decade-chip-all"
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold tracking-wider transition-all border ${
                activeDecade === "all"
                  ? "bg-[#3F2D1B] text-amber-50 border-[#3F2D1B] shadow"
                  : "bg-white text-[#3F2D1B] border-amber-300 hover:border-[#3F2D1B]"
              }`}
            >
              ALL · {allWindows.length}
            </button>
            {decades.map((d) => {
              const count = allWindows.filter((w) => {
                const ds = `${d}-01-01`;
                const de = `${d + 9}-12-31`;
                return !(w.end < ds || w.start > de);
              }).length;
              const active = activeDecade === String(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setActiveDecade(String(d))}
                  data-testid={`decade-chip-${d}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold tracking-wider transition-all border ${
                    active
                      ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-transparent shadow"
                      : "bg-white text-[#3F2D1B] border-amber-300 hover:border-[#3F2D1B]"
                  }`}
                >
                  {d}s · {count}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* === Next 24 months · what to watch === */}
      <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#1B1235] via-[#2A1A4A] to-[#3F2D6B] p-5 sm:p-6 border-2 border-amber-400/40 shadow-inner" data-testid="horizon-24mo-strip">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="label-eyebrow text-amber-300 flex items-center gap-2 text-[10px]">
              <Sparkle size={14} weight="duotone" /> Next 24 months · what to watch
            </div>
            <h3 className="font-serif-display text-2xl sm:text-3xl text-amber-50 font-light mt-1 leading-tight">
              Your actionable horizon
            </h3>
            <p className="text-xs sm:text-sm text-amber-100/80 mt-1.5 font-light">
              Every top-signal window from your timeline that overlaps the next 2 years — sorted in order so you know exactly what's <b>coming up next</b>.
            </p>
          </div>
          <span className="text-[10px] font-bold tracking-wider px-3 py-1 rounded-full bg-amber-400 text-[#3F2D1B] whitespace-nowrap shadow">
            {horizon.items.length} {horizon.items.length === 1 ? "window" : "windows"}
          </span>
        </div>
        {horizon.items.length === 0 ? (
          <div className="mt-5 rounded-xl bg-white/10 border border-amber-200/20 p-5 text-center backdrop-blur-sm" data-testid="horizon-empty">
            <Clock size={26} weight="duotone" className="text-amber-300 mx-auto" />
            <p className="text-sm text-amber-50 font-light mt-3 leading-relaxed max-w-xl mx-auto">
              No high-signal life-insight windows fire in the next 24 months — your current period reads as <b>steady, not transformative</b>.
            </p>
            <p className="text-xs text-amber-100/70 mt-2 max-w-xl mx-auto leading-relaxed">
              Use this time to consolidate gains and prepare. Browse your full timeline below — the next major activation may already be visible on the horizon.
            </p>
          </div>
        ) : (
          <div className="mt-5 flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" data-testid="horizon-24mo-list">
            {horizon.items.map((w, i) => {
              const Icon = w._band.icon;
              const startD = new Date((w.start || "") + "T00:00:00");
              const isLive = w.start <= horizon.todayISO && w.end >= horizon.todayISO;
              const msToStart = startD.getTime() - new Date(horizon.todayISO + "T00:00:00").getTime();
              const daysOut = Math.max(0, Math.round(msToStart / 86400000));
              const startsInLabel = isLive
                ? "LIVE NOW"
                : daysOut < 30
                ? `In ${daysOut}d`
                : daysOut < 365
                ? `In ~${Math.round(daysOut / 30)} mo`
                : `In ~${(daysOut / 365).toFixed(1)} yr`;
              return (
                <div
                  key={i}
                  data-testid={`horizon-card-${i}`}
                  className="flex-shrink-0 w-64 sm:w-72 snap-start rounded-xl bg-white/95 backdrop-blur-sm border-2 border-amber-200/60 p-4 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${w._band.badge}`}>
                      <Icon size={16} weight="fill" />
                    </div>
                    <span
                      className={`text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-full ${
                        isLive
                          ? "bg-emerald-500 text-white animate-pulse"
                          : daysOut < 90
                          ? "bg-amber-200 text-amber-900"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {startsInLabel}
                    </span>
                  </div>
                  <p className="mt-2.5 text-[10px] font-bold tracking-wider uppercase text-[#7C2D12]/80">
                    {w._band.title}
                  </p>
                  <p className="text-sm font-medium text-[#3F2D1B] mt-1 leading-snug">{w.label}</p>
                  <div className="mt-2 text-[11px] text-muted leading-snug">
                    {fmtDate(w.start)} → {fmtDate(w.end)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {LI_BANDS.map((band) => {
          const items = filterByDecade(li[band.key] || []);
          const Icon = band.icon;
          return (
            <div
              key={band.key}
              data-testid={`li-band-${band.key}`}
              className={`rounded-xl p-5 bg-gradient-to-br ${band.grad} border-2 ${band.accent}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${band.badge}`}>
                  <Icon size={18} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif-display text-lg font-light text-[#3F2D1B] leading-tight">{band.title}</h3>
                  <p className="text-[11px] text-muted leading-snug">{band.sub}</p>
                </div>
                <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${band.badge}`}>
                  {items.length}
                </span>
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-muted mt-3 italic">
                  {activeDecade === "all" ? "No qualifying windows in this band." : `No windows in the ${activeDecade}s.`}
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {items.map((w, i) => (
                    <li
                      key={i}
                      data-testid={`li-${band.key}-${i}`}
                      className="rounded-lg bg-white/70 backdrop-blur-sm px-3 py-2 text-xs"
                    >
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <span className="font-medium text-[#3F2D1B]">{w.label}</span>
                        <span className="text-[10px] text-muted whitespace-nowrap">
                          {fmtDate(w.start)} → {fmtDate(w.end)}
                        </span>
                      </div>
                      {(w.signature || w.level) && (
                        <p className="text-[11px] text-[#7C2D12] mt-0.5 leading-snug">
                          {w.signature || `${w.level} · score ${w.score}`}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted mt-6 italic leading-relaxed max-w-3xl">
        Each window is sub-period precise (MD × AD) — scroll the dedicated Jackpot, Reversal and Fame sections below for the full astrologic reasoning, house breakdowns and classical references behind each entry.
      </p>
    </section>
  );
}

function MahadashaCard({ md, isCurrent }) {
  const tint = ELEMENT_TINT[md.element] || "from-slate-50 to-white border-soft";
  return (
    <article className={`rounded-2xl overflow-hidden border-2 bg-gradient-to-br ${tint} ${isCurrent ? "ring-2 ring-amber-400" : ""}`} data-testid={`md-${md.index}`}>
      <div className="p-5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/70 text-[#3F2D1B] border border-soft">MD #{md.index}</span>
          {isCurrent && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-300 text-amber-950">CURRENT</span>}
          {md.is_partial && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">PARTIAL</span>}
          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/70 text-[#3F2D1B] border border-soft capitalize">{md.element}</span>
          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{md.rashi} <span className="text-muted text-base">· Lord {md.rashi_lord}</span></h3>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-muted">Start</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(md.start)}</div>
          </div>
          <div>
            <div className="text-muted">End</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(md.end)}</div>
          </div>
          <div>
            <div className="text-muted">Duration</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">{md.years} yrs</div>
          </div>
          <div>
            <div className="text-muted">Activates</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">H{md.house} · {md.house_theme}</div>
          </div>
        </div>
        {md.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {[...new Set(md.tags)].map((t) => {
              const meta = TAG_META[t];
              if (!meta) return null;
              const Icon = meta.Icon;
              return (
                <span key={t} className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${meta.color} inline-flex items-center gap-1`}>
                  <Icon size={10} weight="duotone" /> {meta.label}
                </span>
              );
            })}
          </div>
        )}
        {md.why_fires?.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs">
            {md.why_fires.map((r, i) => (
              <li key={i} className="flex gap-2 text-[#3F2D1B]/85 leading-snug">
                <Sparkle size={12} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
        {md.notes?.length > 0 && (
          <div className="mt-3 space-y-1.5 text-[11px] italic text-[#3F2D1B]/80 border-l-2 border-violet-300 pl-3">
            {md.notes.map((n, i) => <p key={i}>{n}</p>)}
          </div>
        )}
        {md.antardashas?.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs font-medium text-[#7C2D12] hover:text-amber-700">View 9 antardashas inside this mahadasha</summary>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted">
                  <tr>
                    <th className="text-left py-1 pr-3">Antardasha</th>
                    <th className="text-left py-1 pr-3">Lord</th>
                    <th className="text-left py-1 pr-3">Start</th>
                    <th className="text-left py-1 pr-3">End</th>
                    <th className="text-left py-1 pr-3">Years</th>
                    <th className="text-left py-1">Activates / tags</th>
                  </tr>
                </thead>
                <tbody>
                  {md.antardashas.map((ad, i) => (
                    <tr key={i} className="border-t border-soft/40">
                      <td className="py-1 pr-3 font-medium text-[#3F2D1B]">{ad.rashi}</td>
                      <td className="py-1 pr-3 text-[#3F2D1B]">{ad.rashi_lord}</td>
                      <td className="py-1 pr-3 text-[#3F2D1B]">{fmtDate(ad.start)}</td>
                      <td className="py-1 pr-3 text-[#3F2D1B]">{fmtDate(ad.end)}</td>
                      <td className="py-1 pr-3 text-[#3F2D1B]">{ad.years}</td>
                      <td className="py-1">
                        {ad.house && <span className="text-[10px] text-muted mr-2">H{ad.house}</span>}
                        {(ad.tags || []).slice(0, 4).map((t) => {
                          const meta = TAG_META[t];
                          if (!meta) return null;
                          return (
                            <span key={t} className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 mr-1 rounded ${meta.color} inline-block`}>{meta.label}</span>
                          );
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </article>
  );
}

export default function KalachakraDashaPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/kalachakra-dasha/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='kcd-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute Kalachakra Dasha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <SEO
        title="Kalachakra Dasha Calculator — Free Jaimini's Wheel of Time (Savya / Apsavya)"
        description="Free Kalachakra Dasha calculator — Jaimini's rare wheel-of-time dasha system. Pinpoints marriage timing, wealth lifts, jackpot windows, fame peaks, reversal periods. Validated against Musk, Federer, Trump, MJ, Bloomberg, Ambani. Swiss-Ephemeris precise."
        path="/kalachakra-dasha"
        keywords={[
          "kalachakra dasha calculator", "kalachakra dasha", "kaalchakra dasha calculator",
          "kalachakra dasha free", "jaimini dasha", "jaimini kalachakra dasha",
          "wheel of time dasha", "kalachakra mahadasha", "kalachakra antardasha",
          "savya dasha", "apsavya dasha", "deha dasha", "kalachakra navamsa",
          "marriage timing kalachakra", "wealth lift astrology", "fame astrology",
          "jackpot astrology", "reversal period astrology",
          "rare dasha system", "kalachakra dasha periods", "kalachakra calculator online",
          "kalachakra by date of birth", "vedic life events calculator",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "SoftwareApplication",
              "name": "AstroKuber Kalachakra Dasha Calculator",
              "applicationCategory": "AstrologyApplication",
              "operatingSystem": "Any (Web)",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
              "description": "Compute Jaimini's Kalachakra Dasha — savya / apsavya direction, 9 mahadashas × 9 antardashas with deterministic event tagging for marriage, wealth, fame, reversal and jackpot windows.",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "ratingCount": "1487",
                "bestRating": "5",
              },
            },
            {
              "@type": "HowTo",
              "name": "How to compute your Kalachakra Dasha",
              "step": [
                { "@type": "HowToStep", "name": "Enter your exact date, time and place of birth" },
                { "@type": "HowToStep", "name": "We detect your Moon's pada and compute savya or apsavya direction" },
                { "@type": "HowToStep", "name": "Receive your full 9×9 timeline with mathematically-tagged windows for marriage, wealth, fame and reversal" },
                { "@type": "HowToStep", "name": "Browse your Life Insights Summary — best times to start a business, gain wealth, hit jackpot, peak fame" },
              ],
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://astrokuberchat.com/" },
                { "@type": "ListItem", "position": 2, "name": "All Tools", "item": "https://astrokuberchat.com/tools" },
                { "@type": "ListItem", "position": 3, "name": "Kalachakra Dasha", "item": "https://astrokuberchat.com/kalachakra-dasha" },
              ],
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is Kalachakra Dasha?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Kalachakra Dasha — Jaimini's 'Wheel of Time' — is a rare conditional dasha system that maps your soul's 99-year life arc onto a 9-mahadasha × 9-antardasha grid. It is famously precise for marriage timing, wealth lifts, fame peaks and reversal periods, and is computed by deriving Savya (forward) or Apsavya (reverse) direction from your Moon's nakshatra pada." },
                },
                {
                  "@type": "Question",
                  "name": "How is Kalachakra Dasha different from Vimshottari?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Vimshottari Dasha is rasi-lord-based and runs 120 years. Kalachakra Dasha is pada-based, conditional, and runs 99 years — and Jaimini classical texts treat it as more powerful for predicting external life events (marriage, wealth, sudden gain, fame) when used in tandem with Vimshottari." },
                },
                {
                  "@type": "Question",
                  "name": "Is the Kalachakra Dasha calculator free?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes — the AstroKuber Kalachakra Dasha calculator is completely free with no signup. Enter your birth details and receive your full 9×9 timeline plus a Life Insights Summary." },
                },
                {
                  "@type": "Question",
                  "name": "What event windows does the calculator highlight?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Five bands: (1) Best time to start a business, (2) Best time for gains, (3) Lucky jackpot / sudden-rise windows, (4) Beware / reversal windows, (5) Top fame & authority periods. Each band is filtered to adult-life only and curated chronologically — recent past + current + nearest future." },
                },
                {
                  "@type": "Question",
                  "name": "How accurate is the algorithm?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Validated against the life arcs of Elon Musk (2020-21 Tesla 10x), Trump (1991 bankruptcy, 2016-2024 POTUS), Anil Ambani (2019 collapse), Roger Federer (2009-12 Wimbledon legacy), Michael Jackson (Thriller / Off-The-Wall eras), Cristiano Ronaldo, Bloomberg, Ross Perot and 5 lottery winners." },
                },
              ],
            },
          ],
        }}
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Clock size={14} weight="duotone" /> Kalachakra Dasha · Jaimini's wheel-of-time
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Your <span className="text-gold">life-graph</span> in Kalachakra
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Kalachakra Dasha is the rare dasha system Parashara and Jaimini both endorse for predicting <b>major life turnarounds</b> — marriage timing, wealth lifts, career promotion or demotion, job changes, foreign moves, and the deep redirections that reshape your script. Based on your Moon's nakshatra and pada, we generate the full 9-mahadasha cycle (Savya or Apsavya) with 9 antardashas each, then tag every window with the events it favours.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="kcd-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="kcd-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="kcd-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="kcd-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`kcd-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="kcd-submit">
            {loading ? "Spinning the Kalachakra wheel…" : <>Reveal my Kalachakra timeline <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="kcd-result">
            {/* === LIFE INSIGHTS SUMMARY === */}
            {data.life_insights && <LifeInsightsSummary li={data.life_insights} fmtDate={fmtDate} />}

            {/* === FOUNDATION === */}
            <section className="rounded-2xl p-6 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="kcd-foundation">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Star size={14} weight="duotone" /> Foundation of your Kalachakra
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-muted text-xs">Lagna</div>
                  <div className="font-medium text-[#3F2D1B]">{data.lagna_rashi}</div>
                </div>
                <div>
                  <div className="text-muted text-xs">Moon rashi · nakshatra · pada</div>
                  <div className="font-medium text-[#3F2D1B]">{data.moon_rashi} · {data.moon_nakshatra} · {data.pada}</div>
                </div>
                <div>
                  <div className="text-muted text-xs">Cycle direction</div>
                  <div className="font-medium text-[#3F2D1B] capitalize">{data.direction}</div>
                </div>
                <div>
                  <div className="text-muted text-xs">Starting MD rashi</div>
                  <div className="font-medium text-[#3F2D1B]">{data.starting_rashi}</div>
                </div>
              </div>
              <p className="text-xs text-muted mt-4 font-light italic">{data.balance_note}</p>
              <p className="text-xs text-muted mt-3 font-light">{data.source_note}</p>
            </section>

            {/* === CURRENT MAHADASHA === */}
            <PaywallGate
              tool="kalachakra_dasha"
              toolLabel="Kalachakra Dasha · Wheel of Time"
              birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
              report={data}
            >
              <div className="space-y-10">
              {data.current_mahadasha && (
              <section className="rounded-2xl p-6 bg-gradient-to-br from-amber-50 to-yellow-100 border-2 border-amber-300" data-testid="kcd-current">
                <div className="label-eyebrow text-amber-900 flex items-center gap-2">
                  <Clock size={14} weight="duotone" /> Where you are right now
                </div>
                <h2 className="font-serif-display text-3xl mt-2 font-light text-[#3F2D1B]">
                  Running Mahadasha: <span className="text-gold">{data.current_mahadasha.rashi}</span> <span className="text-muted text-base font-light">· {fmtDate(data.current_mahadasha.start)} → {fmtDate(data.current_mahadasha.end)}</span>
                </h2>
                {data.current_antardasha && (
                  <p className="text-sm mt-2 text-[#3F2D1B] font-light">
                    Within: <b>{data.current_antardasha.rashi}</b> antardasha · {fmtDate(data.current_antardasha.start)} → {fmtDate(data.current_antardasha.end)}
                  </p>
                )}
                <p className="text-sm text-[#3F2D1B] mt-3 font-light leading-snug">
                  Activates <b>House {data.current_mahadasha.house}</b> ({data.current_mahadasha.house_theme}) · ruled by <b>{data.current_mahadasha.rashi_lord}</b> · {data.current_mahadasha.element} element.
                </p>
                {data.current_mahadasha.why_fires?.length > 0 && (
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {data.current_mahadasha.why_fires.map((w, i) => (
                      <li key={i} className="flex gap-2 leading-snug">
                        <Sparkle size={14} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
                        <span className="text-[#3F2D1B]">{w}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {/* === JACKPOT WINDOWS (6 sudden-wealth signatures) === */}
            {data.insight_categories.jackpot_windows?.length > 0 && (
              <section className="rounded-2xl p-6 bg-gradient-to-br from-yellow-50 via-amber-100 to-orange-100 border-2 border-amber-400 shadow-md" data-testid="jackpot-windows">
                <div className="flex items-center gap-2 text-amber-900">
                  <Star size={20} weight="fill" />
                  <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B]">Jackpot Windows · sudden-wealth signatures</h2>
                </div>
                <p className="text-sm text-[#3F2D1B] mt-2 font-light leading-snug">
                  Kalachakra Dasha encodes <b>six distinct sudden-wealth signatures</b> derived from historical lottery winners ($2.04B Powerball, Boston 1994, Wilmington 1989, Seekonk 1992, London EuroMillions 2024) and Elon Musk's 2021 net-worth peak. We surface <b>every</b> applicable signature so you see exactly which classical yoga is firing inside which MD × AD pair.
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-[#3F2D1B] font-light leading-snug">
                  <li><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-2 align-middle"></span><b>Windfall-led (H8 MD → H11 AD)</b> — multi-year H8 MD opens the windfall channel; H11 AD banks it as cash. <i>Boston 1994 · London 2024 · $2B Powerball secondary trigger</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-2 align-middle"></span><b>Gains-led (H11 MD → H8 AD)</b> — H11 MD runs elevated income for years; H8 AD drops a windfall on top of a hot baseline. <i>Elon Musk 2020–21 net-worth spike</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-rose-400 mr-2 align-middle"></span><b>AD-led windfall (H8 AD + sudden-gain tag)</b> — the AD itself is flagged sudden-gain by KCD's rule-engine and runs in your 8H. <i>$2.04B Powerball winner Nov 2022</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-indigo-400 mr-2 align-middle"></span><b>Dharmic-luck (H9 AD in Jupiter-rashi)</b> — 9H Bhagya AD running in Sagittarius or Pisces = Phaladeepika's textbook windfall-via-name. <i>Seekonk Dec 1992</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-fuchsia-400 mr-2 align-middle"></span><b>Dharmic+windfall combo</b> — AD carries BOTH <code>fortune_lift</code> + <code>sudden_gain</code> tags = dharma-backed sudden gain (rare double-tag).</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-2 align-middle"></span><b>Lakshmi-tattva windfall (Jupiter-rashi AD · wealth+fortune)</b> — Sagittarius/Pisces AD with <code>wealth_lift</code> + <code>fortune_lift</code> tags = guru-Lakshmi money-as-grace. <i>Wilmington Sep 1989</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-cyan-400 mr-2 align-middle"></span><b>Magnate wealth crystallization</b> — multi-year wealth-tagged MD in H2/H10/H11 × AD also carrying <code>wealth_lift</code> + (authority/promotion/enterprise/fortune) tags. Not lottery — existing assets revalued at scale. <i>Michael Bloomberg's $4.5B → $16B jump 2008-2009</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-teal-400 mr-2 align-middle"></span><b>Business-exit windfall</b> — MD <code>wealth_lift</code> + <code>enterprise_launch</code> × AD <code>wealth_lift</code> + <code>transfer_movement</code>. The founder cashes out: company itself moves (sale, M&amp;A, exit). <i>Ross Perot's $2.4B EDS-to-GM sale 1984</i>.</li>
                </ul>
                <div className="mt-5 space-y-3">
                  {data.insight_categories.jackpot_windows.map((jw, i) => {
                    const dirBadge =
                      jw.direction === "h11_md_h8_ad" ? "bg-emerald-400 text-emerald-950"
                      : jw.direction === "h8_md_h11_ad" ? "bg-amber-400 text-amber-950"
                      : jw.direction === "h8_ad_sudden_gain" ? "bg-rose-400 text-rose-950"
                      : jw.direction === "h9_jupiter_dharmic" ? "bg-indigo-400 text-indigo-50"
                      : jw.direction === "fortune_sudden_combo" ? "bg-fuchsia-400 text-fuchsia-50"
                      : jw.direction === "jupiter_lakshmi_windfall" ? "bg-yellow-400 text-yellow-950"
                      : jw.direction === "magnate_crystallization" ? "bg-cyan-400 text-cyan-950"
                      : jw.direction === "business_exit_windfall" ? "bg-teal-400 text-teal-950"
                      : "bg-amber-400 text-amber-950";
                    const dirBorder =
                      jw.direction === "h11_md_h8_ad" ? "border-emerald-300"
                      : jw.direction === "h8_ad_sudden_gain" ? "border-rose-300"
                      : jw.direction === "h9_jupiter_dharmic" ? "border-indigo-300"
                      : jw.direction === "fortune_sudden_combo" ? "border-fuchsia-300"
                      : jw.direction === "jupiter_lakshmi_windfall" ? "border-yellow-300"
                      : jw.direction === "magnate_crystallization" ? "border-cyan-300"
                      : jw.direction === "business_exit_windfall" ? "border-teal-300"
                      : "border-amber-300";
                    const sparkleColor =
                      jw.direction === "h11_md_h8_ad" ? "text-emerald-700"
                      : jw.direction === "h8_ad_sudden_gain" ? "text-rose-700"
                      : jw.direction === "h9_jupiter_dharmic" ? "text-indigo-700"
                      : jw.direction === "fortune_sudden_combo" ? "text-fuchsia-700"
                      : jw.direction === "jupiter_lakshmi_windfall" ? "text-yellow-700"
                      : jw.direction === "magnate_crystallization" ? "text-cyan-700"
                      : jw.direction === "business_exit_windfall" ? "text-teal-700"
                      : "text-amber-700";
                    const pillBg =
                      jw.direction === "h11_md_h8_ad" ? "bg-emerald-100 text-emerald-900 border border-emerald-300"
                      : jw.direction === "h8_ad_sudden_gain" ? "bg-rose-100 text-rose-900 border border-rose-300"
                      : jw.direction === "h9_jupiter_dharmic" ? "bg-indigo-100 text-indigo-900 border border-indigo-300"
                      : jw.direction === "fortune_sudden_combo" ? "bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-300"
                      : jw.direction === "jupiter_lakshmi_windfall" ? "bg-yellow-100 text-yellow-900 border border-yellow-300"
                      : jw.direction === "magnate_crystallization" ? "bg-cyan-100 text-cyan-900 border border-cyan-300"
                      : jw.direction === "business_exit_windfall" ? "bg-teal-100 text-teal-900 border border-teal-300"
                      : "bg-amber-100 text-amber-900 border border-amber-300";
                    return (
                      <div key={i} className={`rounded-xl p-5 bg-white border-2 ${dirBorder} relative overflow-hidden`} data-testid={`jackpot-${i}`}>
                        <div className={`absolute top-0 right-0 px-3 py-1 ${dirBadge} text-[10px] font-bold tracking-wider rounded-bl-lg`}>JACKPOT</div>
                        <div className="flex items-baseline gap-2 flex-wrap pr-20">
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-950">MD#{jw.md_index}</span>
                          {jw.direction_label && (
                            <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${pillBg}`}>{jw.direction_label}</span>
                          )}
                          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{jw.label}</h3>
                        </div>
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div>
                            <div className="text-muted">Start</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(jw.start)}</div>
                          </div>
                          <div>
                            <div className="text-muted">End</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(jw.end)}</div>
                          </div>
                          <div>
                            <div className="text-muted">Duration</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">{jw.years} yrs</div>
                          </div>
                          <div>
                            <div className="text-muted">Activates</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">H{jw.md_house} (MD) × H{jw.ad_house} (AD)</div>
                          </div>
                        </div>
                        {jw.headline && (
                          <p className="mt-4 text-sm text-[#3F2D1B] font-medium italic leading-snug">{jw.headline}</p>
                        )}
                        <ul className="mt-3 space-y-1.5 text-sm">
                          {jw.reasons.map((r, idx) => (
                            <li key={idx} className="flex gap-2 leading-snug">
                              <Sparkle size={14} weight="duotone" className={`${sparkleColor} flex-shrink-0 mt-0.5`} />
                              <span className="text-[#3F2D1B]">{r}</span>
                            </li>
                          ))}
                        </ul>
                        {jw.extra_tags?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {jw.extra_tags.filter(t => TAG_META[t]).slice(0, 6).map((t) => {
                              const meta = TAG_META[t];
                              return (
                                <span key={t} className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === REVERSAL WINDOWS (bankruptcy / partnership-collapse) === */}
            {data.insight_categories.reversal_windows?.length > 0 && (
              <section className="rounded-2xl p-6 bg-gradient-to-br from-slate-50 via-zinc-100 to-stone-100 border-2 border-slate-400 shadow-md" data-testid="reversal-windows">
                <div className="flex items-center gap-2 text-slate-900">
                  <ShieldWarning size={20} weight="fill" />
                  <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B]">Reversal Windows · loss & restructuring signatures</h2>
                </div>
                <p className="text-sm text-[#3F2D1B] mt-2 font-light leading-snug">
                  The mirror-image of Jackpot Windows. We detect <b>four classical loss signatures</b> validated against Donald Trump's 1991–1993 casino bankruptcies (Taj Mahal Jul 1991, Castle + Plaza 1992) and his Mar 1992 divorce — all firing simultaneously inside one AD (Aquarius H7, `marriage + major_turnaround`).
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-[#3F2D1B] font-light leading-snug">
                  <li><span className="inline-block w-2 h-2 rounded-full bg-stone-500 mr-2 align-middle"></span><b>Trika-dusthana double</b> — MD H6/8/12 × AD H6/8/12 = sustained loss/transformation. Phaladeepika's classical bankruptcy / health-collapse window.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-zinc-500 mr-2 align-middle"></span><b>AD-led setback</b> — H6/H8/H12 AD with <code>struggle_dusthana</code> tag and no wealth-positive offset.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-slate-700 mr-2 align-middle"></span><b>Maraka partnership collapse</b> — H7 AD with <code>major_turnaround</code>. Parashara's textbook MARAKA signature: partnerships, contracts, marriage AND business deals rupture together. <i>Trump's 1991-93 bankruptcies + divorce</i>.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-neutral-500 mr-2 align-middle"></span><b>H2 wealth-drainage</b> — H2 (2nd Maraka) AD with struggle/turnaround tags = direct savings/family-money depletion.</li>
                  <li><span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-2 align-middle"></span><b>H12 forced asset-drainage</b> — H12 AD with <code>struggle_dusthana</code> + <code>transfer_movement</code> = court-ordered asset liquidation / foreign-debt settlement / insolvency. <i>Anil Ambani's UK Supreme Court ruling 22 Feb 2019 → Sagittarius AD H12 started 23 Feb 2019, spanning the full UK liquidation + India NCLT bankruptcy through Dec 2020</i>.</li>
                </ul>
                <div className="mt-5 space-y-3">
                  {data.insight_categories.reversal_windows.map((rw, i) => {
                    const dirBg =
                      rw.direction === "maraka_collapse" ? "bg-slate-700 text-slate-50"
                      : rw.direction === "trika_double" ? "bg-stone-600 text-stone-50"
                      : rw.direction === "h2_maraka_drainage" ? "bg-neutral-600 text-neutral-50"
                      : rw.direction === "h12_asset_drainage" ? "bg-red-700 text-red-50"
                      : "bg-zinc-500 text-zinc-50";
                    const dirBorder =
                      rw.direction === "maraka_collapse" ? "border-slate-300"
                      : rw.direction === "trika_double" ? "border-stone-300"
                      : rw.direction === "h2_maraka_drainage" ? "border-neutral-300"
                      : rw.direction === "h12_asset_drainage" ? "border-red-300"
                      : "border-zinc-300";
                    const dirPill =
                      rw.direction === "maraka_collapse" ? "bg-slate-100 text-slate-900 border border-slate-300"
                      : rw.direction === "trika_double" ? "bg-stone-100 text-stone-900 border border-stone-300"
                      : rw.direction === "h2_maraka_drainage" ? "bg-neutral-100 text-neutral-900 border border-neutral-300"
                      : rw.direction === "h12_asset_drainage" ? "bg-red-100 text-red-900 border border-red-300"
                      : "bg-zinc-100 text-zinc-900 border border-zinc-300";
                    return (
                      <div key={i} className={`rounded-xl p-5 bg-white border-2 ${dirBorder} relative overflow-hidden`} data-testid={`reversal-${i}`}>
                        <div className={`absolute top-0 right-0 px-3 py-1 ${dirBg} text-[10px] font-bold tracking-wider rounded-bl-lg`}>REVERSAL</div>
                        <div className="flex items-baseline gap-2 flex-wrap pr-20">
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-stone-200 text-stone-950">MD#{rw.md_index}</span>
                          {rw.direction_label && (
                            <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${dirPill}`}>{rw.direction_label}</span>
                          )}
                          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{rw.label}</h3>
                        </div>
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div><div className="text-muted">Start</div><div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(rw.start)}</div></div>
                          <div><div className="text-muted">End</div><div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(rw.end)}</div></div>
                          <div><div className="text-muted">Duration</div><div className="font-medium text-[#3F2D1B] mt-0.5">{rw.years} yrs</div></div>
                          <div><div className="text-muted">Activates</div><div className="font-medium text-[#3F2D1B] mt-0.5">H{rw.md_house} (MD) × H{rw.ad_house} (AD)</div></div>
                        </div>
                        {rw.headline && (
                          <p className="mt-4 text-sm text-[#3F2D1B] font-medium italic leading-snug">{rw.headline}</p>
                        )}
                        <ul className="mt-3 space-y-1.5 text-sm">
                          {rw.reasons.map((r, idx) => (
                            <li key={idx} className="flex gap-2 leading-snug">
                              <Warning size={14} weight="duotone" className="text-slate-700 flex-shrink-0 mt-0.5" />
                              <span className="text-[#3F2D1B]">{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === FAME WINDOWS (combined MD+AD fame-score) === */}
            {data.insight_categories.fame_windows?.length > 0 && (
              <section className="rounded-2xl p-6 bg-gradient-to-br from-fuchsia-50 via-pink-100 to-rose-100 border-2 border-fuchsia-400 shadow-md" data-testid="fame-windows">
                <div className="flex items-center gap-2 text-fuchsia-900">
                  <Crown size={20} weight="fill" />
                  <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B]">Fame Windows · viral visibility yoga</h2>
                </div>
                <p className="text-sm text-[#3F2D1B] mt-2 font-light leading-snug">
                  When KCD's running MD <em>and</em> AD <b>both</b> activate fame-channels (H1 self-brand, H5 creative-fans, H10 public-office, H11 mass-following) or your natal Sun/lagna-lord/10L signs — the visibility curve spikes. This is the exact configuration that lit up for Donald Trump's <i>Apprentice</i> launch (2003–2005), Addison Rae's TikTok rise (2020–2022), and the Ludhiana virality wave (2022–2026). Scored windows — <b>viral</b> (≥10), <b>high</b> (≥7), <b>moderate</b> (≥4) — sorted by intensity.
                </p>
                <div className="mt-5 space-y-3">
                  {data.insight_categories.fame_windows.map((fw, i) => {
                    const levelStyle = fw.level === "viral"
                      ? { badge: "bg-fuchsia-500 text-white", border: "border-fuchsia-400" }
                      : fw.level === "high"
                      ? { badge: "bg-pink-400 text-pink-950", border: "border-pink-300" }
                      : { badge: "bg-rose-200 text-rose-900", border: "border-rose-200" };
                    return (
                      <div key={i} className={`rounded-xl p-5 bg-white border-2 ${levelStyle.border} relative overflow-hidden`} data-testid={`fame-${i}`}>
                        <div className={`absolute top-0 right-0 px-3 py-1 ${levelStyle.badge} text-[10px] font-bold tracking-wider rounded-bl-lg uppercase`}>{fw.level} · {fw.score}</div>
                        <div className="flex items-baseline gap-2 flex-wrap pr-24">
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-200 text-fuchsia-950">MD#{fw.md_index}</span>
                          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{fw.label}</h3>
                        </div>
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div>
                            <div className="text-muted">Start</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(fw.start)}</div>
                          </div>
                          <div>
                            <div className="text-muted">End</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">{fmtDate(fw.end)}</div>
                          </div>
                          <div>
                            <div className="text-muted">Duration</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">{fw.years} yrs</div>
                          </div>
                          <div>
                            <div className="text-muted">Activates</div>
                            <div className="font-medium text-[#3F2D1B] mt-0.5">H{fw.md_house} (MD) × H{fw.ad_house} (AD)</div>
                          </div>
                        </div>
                        {fw.md_factors?.length > 0 && (
                          <div className="mt-4">
                            <div className="label-eyebrow text-fuchsia-900 text-[10px]">MD {fw.md_rashi} fame factors</div>
                            <ul className="mt-1 space-y-1 text-sm">
                              {fw.md_factors.map((f, idx) => (
                                <li key={idx} className="flex gap-2 leading-snug">
                                  <Sparkle size={12} weight="duotone" className="text-fuchsia-700 flex-shrink-0 mt-1" />
                                  <span className="text-[#3F2D1B]">{f}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {fw.ad_factors?.length > 0 && (
                          <div className="mt-3">
                            <div className="label-eyebrow text-pink-900 text-[10px]">AD {fw.ad_rashi} fame factors</div>
                            <ul className="mt-1 space-y-1 text-sm">
                              {fw.ad_factors.map((f, idx) => (
                                <li key={idx} className="flex gap-2 leading-snug">
                                  <Sparkle size={12} weight="duotone" className="text-pink-700 flex-shrink-0 mt-1" />
                                  <span className="text-[#3F2D1B]">{f}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === INSIGHT CATEGORIES === */}
            <section className="space-y-6" data-testid="kcd-categories">
              <div className="label-eyebrow text-[#7C2D12]">Major life windows in your Kalachakra cycle</div>
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const windows = data.insight_categories[key] || [];
                if (windows.length === 0) return null;
                const Icon = meta.Icon;
                return (
                  <div key={key} className="rounded-2xl p-5 bg-white border border-soft" data-testid={`cat-${key}`}>
                    <div className={`flex items-center gap-2 ${meta.color}`}>
                      <Icon size={16} weight="duotone" />
                      <h3 className="font-medium">{meta.label}</h3>
                      <span className="text-xs text-muted">({windows.length} {windows.length === 1 ? "window" : "windows"})</span>
                    </div>
                    <p className="text-xs text-muted mt-1 font-light">{meta.blurb}</p>
                    <div className="mt-3 space-y-2">
                      {windows.map((w) => (
                        <div key={w.index} className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4 p-3 rounded-md bg-[#FAF4E8] border border-soft text-sm">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900">MD#{w.index}</span>
                            <span className="font-medium text-[#3F2D1B]">{w.rashi}</span>
                            <span className="text-xs text-muted">H{w.house}</span>
                          </div>
                          <div className="text-xs text-[#3F2D1B] font-light">
                            {fmtDate(w.start)} → {fmtDate(w.end)} <span className="text-muted">· {w.years}y</span>
                          </div>
                          {w.why_fires?.[0] && <div className="text-xs text-muted italic flex-1">{w.why_fires[0]}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>

            {/* === FULL MAHADASHA TIMELINE === */}
            <section className="space-y-4" data-testid="kcd-timeline">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Crown size={14} weight="duotone" /> Full Kalachakra timeline · 9 mahadashas with 9 antardashas each
              </div>
              {data.mahadashas.map((md) => (
                <MahadashaCard
                  key={md.index}
                  md={md}
                  isCurrent={data.current_mahadasha && data.current_mahadasha.index === md.index}
                />
              ))}
            </section>
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Jaimini"
              pitch="Get a 1:1 walk-through of which specific antardashas within your current MD will deliver marriage, promotion or that long-awaited wealth lift — with a Jaimini Kalachakra specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
